@file:Suppress("TooManyFunctions", "UnsafeCastFromDynamic")

package top.wcpe.allincore.ui.web.model

import kotlin.js.Promise
import kotlin.js.json
import kotlin.math.PI
import kotlin.math.abs

internal class ThreeModelBuildException(
    val code: String,
    message: String,
) : RuntimeException(message)

internal class ThreeSceneBundle(
    val scene: dynamic,
    val viewRoot: dynamic,
    val contentRoot: dynamic,
    val camera: dynamic,
    private val geometries: MutableList<dynamic>,
    private val materials: MutableList<dynamic>,
    private val textures: MutableList<dynamic>,
    private val urlLeases: MutableList<ThreeTextureUrlLease>,
) {
    private var disposed = false

    fun dispose() {
        if (disposed) return
        disposed = true
        geometries.forEach(::disposeDynamic)
        materials.forEach(::disposeDynamic)
        textures.forEach(::disposeDynamic)
        urlLeases.forEach { runCatching(it::dispose) }
        runCatching { scene.clear() }
        geometries.clear()
        materials.clear()
        textures.clear()
        urlLeases.clear()
    }
}

internal class ThreeSceneBuilder(
    private val module: dynamic,
    private val assetLoader: ThreeModelAssetLoader,
) {
    fun build(modelAsset: ThreeModelAsset, model: RestrictedBbModel): Promise<ThreeSceneBundle> {
        val session = BuildSession(module, assetLoader, modelAsset, model)
        return runCatching(session::build).getOrElse(::rejectedPromise)
    }
}

private class BuildSession(
    private val module: dynamic,
    private val assetLoader: ThreeModelAssetLoader,
    private val modelAsset: ThreeModelAsset,
    private val model: RestrictedBbModel,
) {
    private val urlLeases = mutableListOf<ThreeTextureUrlLease>()
    private val textures = mutableListOf<dynamic>()
    private val geometries = mutableListOf<dynamic>()
    private val materials = mutableListOf<dynamic>()
    private val texturesBySource = mutableMapOf<RestrictedBbTextureSource, dynamic>()
    private val textureDeclarations = model.textures.associateBy(RestrictedBbTexture::index)
    private val materialCache = mutableMapOf<MaterialKey, dynamic>()
    private var ownershipTransferred = false

    fun build(): Promise<ThreeSceneBundle> {
        validateBudgets()
        return loadTextures(uniqueTextures(), 0).then { createBundle() }.catch { reason ->
            disposeCandidate()
            throw asThrowable(reason)
        }
    }

    private fun validateBudgets() {
        requireCapacity(model.cubes.size, model.gpuBudget.geometryObjects, RestrictedBbModelLimits.GEOMETRIES, "geometry")
        requireCapacity(materialKeys().size, model.gpuBudget.materialObjects, RestrictedBbModelLimits.MATERIALS, "material")
        requireCapacity(uniqueTextures().size, model.gpuBudget.textureObjects, RestrictedBbModelLimits.TEXTURES, "texture")
    }

    private fun uniqueTextures(): List<RestrictedBbTexture> =
        model.textures.distinctBy(RestrictedBbTexture::source)

    private fun materialKeys(): Set<MaterialKey> = model.cubes
        .flatMap(RestrictedBbCube::faces)
        .mapTo(mutableSetOf()) { face ->
            val declaration = textureDeclarations.getValue(face.textureIndex)
            MaterialKey(effectiveTextureIdentity(declaration), face.direction)
        }

    private fun loadTextures(declarations: List<RestrictedBbTexture>, index: Int): Promise<Unit> {
        if (index >= declarations.size) return Promise.resolve(Unit)
        val declaration = declarations[index]
        return loadTextureLease(declaration).thenPromise { lease ->
            urlLeases += lease
            loadThreeTexture(lease.url)
        }.thenPromise { texture ->
            configureTexture(texture)
            texturesBySource[declaration.source] = texture
            loadTextures(declarations, index + 1)
        }
    }

    private fun loadTextureLease(texture: RestrictedBbTexture): Promise<ThreeTextureUrlLease> =
        runCatching { assetLoader.loadTexture(modelAsset, texture) }.getOrElse { error ->
            rejectedPromise(ThreeModelBuildException(TEXTURE_ASSET_FAILED, "模型纹理资产加载失败：${safeMessage(error)}"))
        }

    private fun loadThreeTexture(url: String): Promise<dynamic> = Promise { resolve, reject ->
        try {
            val loader = construct(module.TextureLoader)
            val loaded = loader.load(
                url,
                { texture: dynamic ->
                    retainTexture(texture)
                    resolve(texture)
                },
                null,
                { reason: dynamic ->
                    reject(ThreeModelBuildException(TEXTURE_DECODE_FAILED, "Three 纹理解码失败：${safeReason(reason)}"))
                },
            )
            retainTexture(loaded)
        } catch (error: Throwable) {
            reject(ThreeModelBuildException(TEXTURE_DECODE_FAILED, "Three 纹理解码失败：${safeMessage(error)}"))
        }
    }

    private fun retainTexture(texture: dynamic) {
        if (textures.any { it === texture }) return
        requireCapacity(textures.size + 1, model.gpuBudget.textureObjects, RestrictedBbModelLimits.TEXTURES, "texture")
        textures.add(texture)
    }

    private fun configureTexture(texture: dynamic) {
        texture.colorSpace = module.SRGBColorSpace
        texture.magFilter = module.NearestFilter
        texture.minFilter = module.NearestFilter
        texture.needsUpdate = true
    }

    private fun createBundle(): ThreeSceneBundle {
        val scene = construct(module.Scene)
        val viewRoot = construct(module.Group)
        val contentRoot = construct(module.Group)
        scene.add(viewRoot)
        viewRoot.add(contentRoot)
        addLights(scene)
        buildHierarchy(contentRoot)
        centerContent(contentRoot)
        val camera = construct(module.OrthographicCamera, -1.0, 1.0, 1.0, -1.0, 0.01, 1000.0)
        ownershipTransferred = true
        return ThreeSceneBundle(scene, viewRoot, contentRoot, camera, geometries, materials, textures, urlLeases)
    }

    private fun addLights(scene: dynamic) {
        scene.add(construct(module.AmbientLight, 0xFFFFFF, AMBIENT_INTENSITY))
        val directional = construct(module.DirectionalLight, 0xFFFFFF, DIRECTIONAL_INTENSITY)
        directional.position.set(4.0, 8.0, 6.0)
        scene.add(directional)
    }

    private fun buildHierarchy(contentRoot: dynamic) {
        val cubes = model.cubes.associateBy(RestrictedBbCube::uuid)
        model.roots.forEach { node -> contentRoot.add(buildNode(node, ZERO_VECTOR, cubes)) }
    }

    private fun buildNode(
        node: RestrictedBbOutlinerNode,
        parentOrigin: BbVector3,
        cubes: Map<String, RestrictedBbCube>,
    ): dynamic = when (node) {
        is RestrictedBbGroupNode -> buildGroup(node, parentOrigin, cubes)
        is RestrictedBbCubeNode -> buildCube(cubes.getValue(node.cubeUuid), parentOrigin)
    }

    private fun buildGroup(
        group: RestrictedBbGroupNode,
        parentOrigin: BbVector3,
        cubes: Map<String, RestrictedBbCube>,
    ): dynamic {
        val pivot = construct(module.Group)
        setPosition(pivot, group.origin.minus(parentOrigin))
        setRotation(pivot, group.rotation)
        group.children.forEach { child -> pivot.add(buildNode(child, group.origin, cubes)) }
        return pivot
    }

    private fun buildCube(cube: RestrictedBbCube, parentOrigin: BbVector3): dynamic {
        requireCapacity(geometries.size + 1, model.gpuBudget.geometryObjects, RestrictedBbModelLimits.GEOMETRIES, "geometry")
        val geometry = construct(
            module.BoxGeometry,
            abs(cube.to.x - cube.from.x),
            abs(cube.to.y - cube.from.y),
            abs(cube.to.z - cube.from.z),
        )
        geometries.add(geometry)
        val faces = cube.faces.associateBy(RestrictedBbFace::direction)
        applyUvs(geometry, faces)
        val faceMaterials = BOX_FACE_ORDER.map { direction -> materialFor(faces.getValue(direction)) }.toTypedArray()
        val mesh = construct(module.Mesh, geometry, faceMaterials)
        setPosition(mesh, cube.center().minus(cube.origin))
        val pivot = construct(module.Group)
        setPosition(pivot, cube.origin.minus(parentOrigin))
        setRotation(pivot, cube.rotation)
        pivot.add(mesh)
        return pivot
    }

    private fun applyUvs(geometry: dynamic, faces: Map<BbFaceDirection, RestrictedBbFace>) {
        val attribute = geometry.getAttribute("uv")
        BOX_FACE_ORDER.forEachIndexed { faceIndex, direction ->
            val uv = faces.getValue(direction).uv
            val left = uv.u1 / model.resolution.width
            val right = uv.u2 / model.resolution.width
            val top = 1.0 - uv.v1 / model.resolution.height
            val bottom = 1.0 - uv.v2 / model.resolution.height
            setFaceUv(attribute, faceIndex * VERTICES_PER_FACE, left, right, top, bottom)
        }
        attribute.needsUpdate = true
    }

    private fun materialFor(face: RestrictedBbFace): dynamic {
        val declaration = textureDeclarations.getValue(face.textureIndex)
        val key = MaterialKey(effectiveTextureIdentity(declaration), face.direction)
        val cached = materialCache[key]
        if (cached != null) return cached
        requireCapacity(materials.size + 1, model.gpuBudget.materialObjects, RestrictedBbModelLimits.MATERIALS, "material")
        val texture = texturesBySource.getValue(declaration.source)
        val material = construct(
            module.MeshStandardMaterial,
            json("map" to texture, "transparent" to true, "alphaTest" to ALPHA_TEST),
        )
        materials.add(material)
        materialCache[key] = material
        return material
    }

    private fun centerContent(contentRoot: dynamic) {
        contentRoot.updateMatrixWorld(true)
        val bounds = construct(module.Box3).setFromObject(contentRoot)
        val center = construct(module.Vector3)
        bounds.getCenter(center)
        contentRoot.position.sub(center)
        contentRoot.updateMatrixWorld(true)
    }

    private fun disposeCandidate() {
        if (ownershipTransferred) return
        geometries.forEach(::disposeDynamic)
        materials.forEach(::disposeDynamic)
        textures.forEach(::disposeDynamic)
        urlLeases.forEach { runCatching(it::dispose) }
        geometries.clear()
        materials.clear()
        textures.clear()
        urlLeases.clear()
    }
}

private data class MaterialKey(
    val textureIdentity: Int,
    val direction: BbFaceDirection,
)

private fun effectiveTextureIdentity(texture: RestrictedBbTexture): Int =
    if (texture.sourceKind == RestrictedBbTextureSourceKind.DSL_OVERRIDE) DSL_OVERRIDE_TEXTURE_ID else texture.index

private fun BbVector3.minus(other: BbVector3): BbVector3 = BbVector3(x - other.x, y - other.y, z - other.z)

private fun RestrictedBbCube.center(): BbVector3 = BbVector3(
    (from.x + to.x) / 2.0,
    (from.y + to.y) / 2.0,
    (from.z + to.z) / 2.0,
)

private fun setPosition(target: dynamic, value: BbVector3) {
    target.position.set(value.x, value.y, value.z)
}

private fun setRotation(target: dynamic, value: BbVector3) {
    target.rotation.set(value.x.toRadians(), value.y.toRadians(), value.z.toRadians())
}

private fun Double.toRadians(): Double = this * PI / HALF_TURN_DEGREES

private fun setFaceUv(
    attribute: dynamic,
    start: Int,
    left: Double,
    right: Double,
    top: Double,
    bottom: Double,
) {
    attribute.setXY(start, left, top)
    attribute.setXY(start + 1, right, top)
    attribute.setXY(start + 2, left, bottom)
    attribute.setXY(start + 3, right, bottom)
}

private fun requireCapacity(actual: Int, budget: Int, hardLimit: Int, name: String) {
    if (actual > budget) {
        throw ThreeModelBuildException(GPU_BUDGET_EXCEEDED, "$name 对象数超过解析器 GPU 预算：$actual > $budget")
    }
    if (actual > hardLimit) {
        throw ThreeModelBuildException(GPU_BUDGET_EXCEEDED, "$name 对象数超过硬上限：$actual > $hardLimit")
    }
}

private fun construct(constructor: dynamic, vararg args: dynamic): dynamic =
    js("Reflect.construct(constructor, args)")

private fun disposeDynamic(resource: dynamic) {
    runCatching { resource.dispose() }
}

private fun safeMessage(error: Throwable): String = error.message?.takeIf(String::isNotBlank) ?: "未知错误"

private fun safeReason(reason: dynamic): String = reason?.toString()?.takeIf(String::isNotBlank) ?: "未知错误"

private fun asThrowable(reason: dynamic): Throwable = reason as? Throwable ?: RuntimeException(safeReason(reason))

private fun <T> rejectedPromise(error: Throwable): Promise<T> = Promise { _, reject -> reject(error) }

private fun <T, R> Promise<T>.thenPromise(transform: (T) -> Promise<R>): Promise<R> =
    then(transform).unsafeCast<Promise<R>>()

private val ZERO_VECTOR = BbVector3(0.0, 0.0, 0.0)
private val BOX_FACE_ORDER = listOf(
    BbFaceDirection.EAST,
    BbFaceDirection.WEST,
    BbFaceDirection.UP,
    BbFaceDirection.DOWN,
    BbFaceDirection.SOUTH,
    BbFaceDirection.NORTH,
)

private const val VERTICES_PER_FACE = 4
private const val HALF_TURN_DEGREES = 180.0
private const val AMBIENT_INTENSITY = 1.4
private const val DIRECTIONAL_INTENSITY = 2.2
private const val ALPHA_TEST = 0.001
private const val DSL_OVERRIDE_TEXTURE_ID = -1
internal const val GPU_BUDGET_EXCEEDED = "THREE_MODEL_GPU_BUDGET_EXCEEDED"
internal const val TEXTURE_ASSET_FAILED = "THREE_MODEL_TEXTURE_ASSET_FAILED"
internal const val TEXTURE_DECODE_FAILED = "THREE_MODEL_TEXTURE_DECODE_FAILED"
